import { ref, computed, watch } from "vue";
import appState from "@/appState.js";
import { updateUserPreference } from "@/services/coreCommonDataService/user.js";
import { useToastUtil } from "@/utilities/useToastUtil.js";
import { omitBy, isNil } from "lodash";
import EventBus from "@/utilities/eventBus.js";

export function useTablePreferences(preferenceKey, disableWatcher) {
  const toastUtil = useToastUtil();
  const { user } = appState;
  const { preferences: p } = user;

  const tablePreferences = computed(() => {
    try {
      let parsedPreferences = JSON.parse(p[preferenceKey]);
      ["", "0", "1"].forEach(c => {
        if (parsedPreferences[`columns${c}`]) parsedPreferences[`columns${c}`] = parsedPreferences[`columns${c}`].split(",");
      });
      return parsedPreferences;
    } catch {
     return {};
    } 
  });

  const getAlphaSortOrder = () => {
    if (tablePreferences.value.sortOrder === 1) return "asc";
    else if (tablePreferences.value.sortOrder === -1) return "desc";
    else return undefined;
  };

  const stringifyUserPreferences = (userPreferences = { ...tablePreferences.value, columns: tablePreferences.value.columns?.join(',') }) => {
    return JSON.stringify(omitBy(userPreferences, isNil));
  }

  const updateTablePreferences = (newPreferences) => {
    user.preferences[preferenceKey] = stringifyUserPreferences({
      ...{
        ...tablePreferences.value,
        columns: tablePreferences.value.columns?.join(',')
      },
      ...newPreferences
    });
  }

  if (!disableWatcher) {
    watch(tablePreferences, async () => {
      try {
        const response = await updateUserPreference({
          name: preferenceKey,
          value: stringifyUserPreferences()
        })
  
        if (response.httpError) throw new Error(response.httpErrorDetails);
        else EventBus.emit("preferences-response", { status: 'success' });
      } catch (err) {
        console.error(`Error saving user preferences for ${preferenceKey}`, err);
        EventBus.emit("preferences-response", { status: 'error', details: err.message });
      }
    })
  }

  const saveTablePreferences = (newTablePreferences, successMessage) => new Promise ((resolve, reject) => { 
    try {
      ["", 0, 1].forEach(i => {
        if (newTablePreferences[`columns${i}`]) newTablePreferences[`columns${i}`] = newTablePreferences[`columns${i}`].join(",");
      });
      updateUserPreference({ name: preferenceKey, value: JSON.stringify(newTablePreferences) }).then(data => {
        if (data.httpError) { reject(data.httpErrorDetails); return;  }
        appState.user.preferences[preferenceKey] = JSON.stringify(newTablePreferences);
        if (successMessage) toastUtil.success(successMessage);
        resolve();
      });
    } catch(error) {
      reject(error);
    }
  });

  return { tablePreferences, getAlphaSortOrder, updateTablePreferences, saveTablePreferences };
}
